import type { DynamicFieldComponentProps, JsonDataItem } from '@myosh/odin-components';
import { useEffect, useRef, useState } from 'react';
import { Observable, skipWhile, Subject, take, takeUntil } from 'rxjs';
import type { RecordResult } from '../../../@types/records';
import { forceAssert, isEmptyObject } from '../../../utils/type-guards';
import WrapperStyledButtonField from '../../common/wrapper-styled-button-field';
import EmailThisModal from './email-this-modal.component';

export interface EmailThisButtonProps extends DynamicFieldComponentProps<unknown> {
  buttonCaption?: string;
  recordId?: number;
  saveRecord?: () => Observable<RecordResult>;
  help?: JsonDataItem;
  keepEmailAudit?: boolean;
  includeAttachment?: boolean;
  attachGeneratedDoc?: boolean;
  emailThisDefaultEmailSubject?: string;
  emailThisDefaultEmailBody?: string;
}

function EmailThisButton({ id, recordId, buttonCaption, saveRecord, readOnly, ...rest }: EmailThisButtonProps) {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const destroySubject = useRef(new Subject<void>());

  useEffect(() => {
    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
    };
  }, []);

  const onOpenModal = () => {
    if (recordId === undefined) {
      const resultObservable = saveRecord?.();
      if (resultObservable) {
        resultObservable
          .pipe(
            skipWhile((result) => isEmptyObject(forceAssert<Record<string, unknown>>(result))),
            take(1),
            takeUntil(destroySubject.current)
          )
          .subscribe(() => {
            setShowEmailModal(true);
          });
      }
    } else {
      setShowEmailModal(true);
    }
  };
  const onCloseModal = () => setShowEmailModal(false);

  return (
    <>
      <WrapperStyledButtonField onClick={onOpenModal} disabled={readOnly} buttonCaption={buttonCaption} icon="Mail" />
      {showEmailModal && (
        <EmailThisModal
          fieldId={Number(id)}
          recordId={recordId}
          visible={showEmailModal}
          showCloseIcon={true}
          hidden={onCloseModal}
          readOnly={readOnly}
          {...rest}
        />
      )}
    </>
  );
}

export default EmailThisButton;
